export interface TimelineStep {
  n: number;
  title: string;
  body: string;
}

export interface TimelinePhase {
  id: string;
  name: string;
  duration: string;
  summary: string;
  steps: TimelineStep[];
  checklist: string[];
}

export const PHASES: TimelinePhase[] = [
  {
    id: "pre-work",
    name: "Pre-Work",
    duration: "2 to 4 weeks",
    summary:
      "Everything that happens before you look at a single listing. Skipping this phase is the single most common reason buyers lose a house they wanted.",
    steps: [
      {
        n: 1,
        title: "Pull your credit and read the actual report",
        body: "Lenders pull mortgage-specific FICO versions, which can differ from the score in a banking app. Look for collections, late payments, and balances above 30% of a card's limit. Fixing an error takes weeks, so start here rather than after an application.",
      },
      {
        n: 2,
        title: "Build a cash-to-close number, not just a down payment number",
        body: "Cash to close is the down payment plus closing costs plus prepaid taxes and insurance plus your earnest money, minus any credits. In many markets that is several thousand dollars more than the down payment alone.",
      },
      {
        n: 3,
        title: "Check your debt-to-income ratio",
        body: "Add your monthly debt payments and divide by gross monthly income. Most loan programs want the total housing plus debt ratio in the low-to-mid 40s, with exceptions. A single car loan can move what you qualify for by tens of thousands.",
      },
      {
        n: 4,
        title: "Gather documents before you apply",
        body: "Two years of W-2s or returns, 30 days of pay stubs, two months of full bank statements including every page, and identification. Self-employed buyers should expect requests for profit-and-loss statements and business returns.",
      },
      {
        n: 5,
        title: "Compare lenders and get pre-approved",
        body: "Get quotes from more than one lender within a short shopping window so the credit inquiries are treated as one event. A pre-approval is an underwriter-backed letter, not the instant pre-qualification a website produces from self-reported numbers.",
      },
      {
        n: 6,
        title: "Choose an agent and put the terms in writing",
        body: "Interview more than one. Since 2024, buyers sign a written agreement with their agent before touring, and it states how that agent is paid. How compensation is handled varies by market and by contract, so read the amount and the source before signing.",
      },
    ],
    checklist: [
      "Credit report reviewed and disputes filed",
      "Cash-to-close estimate written down",
      "Debt-to-income ratio calculated",
      "Income, asset, and ID documents collected",
      "Pre-approval letter in hand from at least two lenders",
      "Written buyer agency agreement reviewed and signed",
    ],
  },
  {
    id: "house-hunt",
    name: "House Hunt",
    duration: "1 to 4 months",
    summary:
      "The most variable phase in the whole timeline. Inventory, price band, and how competitive your market is decide whether this takes three weekends or half a year.",
    steps: [
      {
        n: 7,
        title: "Set search criteria and a real ceiling",
        body: "Separate what you need from what you want, and set a monthly-payment ceiling rather than a purchase-price ceiling. Taxes and insurance vary enough between neighborhoods that two homes at the same price can differ by hundreds a month.",
      },
      {
        n: 8,
        title: "Tour homes and take structured notes",
        body: "Photograph the mechanicals, the roofline, the basement or crawlspace, and any water staining. After six showings they blend together, and those photos are what you will argue about later.",
      },
      {
        n: 9,
        title: "Research the neighborhood, not just the house",
        body: "Look at the tax history, insurance quotes for the address, flood and hazard exposure, HOA dues and rules, and commute times at the hours you actually travel.",
      },
      {
        n: 10,
        title: "Line up your lender for a fast offer",
        body: "Ask your loan officer for an updated pre-approval matched to the offer price, and confirm they can turn around a letter same-day. In competitive markets, offer speed is a real advantage.",
      },
    ],
    checklist: [
      "Must-have and nice-to-have lists separated",
      "Monthly payment ceiling set, not just price",
      "Photos and notes captured for every showing",
      "Tax, insurance, HOA, and hazard research done on finalists",
      "Lender confirmed for same-day pre-approval updates",
    ],
  },
  {
    id: "offer",
    name: "Offer",
    duration: "1 to 3 days",
    summary:
      "From writing an offer to a signed contract is usually a matter of days. Sellers commonly respond within 24 to 48 hours, and counteroffers move faster than that.",
    steps: [
      {
        n: 11,
        title: "Write the offer with terms, not just a price",
        body: "Price, closing date, contingencies for financing, appraisal, and inspection, seller credits, and what conveys with the home are all negotiable. Waiving a contingency is a real financial risk, not a formality.",
      },
      {
        n: 12,
        title: "Negotiate the counteroffer",
        body: "Expect at least one round. Credits toward closing costs are often easier for a seller to accept than a price cut, and can help more with your cash to close.",
      },
      {
        n: 13,
        title: "Go under contract and deliver earnest money",
        body: "Once both parties sign, earnest money is typically due within one to three business days and goes to the escrow or title company, never directly to the seller. It is credited to you at closing.",
      },
    ],
    checklist: [
      "Contingencies reviewed line by line with your agent",
      "Closing date coordinated with your lease or current sale",
      "Earnest money wired to escrow within the contract deadline",
      "Fully executed contract copy saved and sent to your lender",
    ],
  },
  {
    id: "under-contract",
    name: "Under Contract",
    duration: "30 to 45 days",
    summary:
      "The structured part of the process, run on contract deadlines. Nearly every task here has a date attached, and missing one can cost you the contract or your earnest money.",
    steps: [
      {
        n: 14,
        title: "Schedule and attend the home inspection",
        body: "Usually within the first week to ten days. Attend if you can. The report separates safety and structural issues from cosmetic ones, and that distinction is what you negotiate on.",
      },
      {
        n: 15,
        title: "Negotiate repairs or credits",
        body: "You can request repairs, a credit, a price reduction, or walk away within the inspection window. Credits are frequently cleaner than repairs because you control the work and the timing.",
      },
      {
        n: 16,
        title: "Lock your rate and complete the appraisal",
        body: "The lender orders the appraisal after inspection resolution. Rate locks typically run 30 to 60 days; talk with your loan officer about locking once you have a firm closing date, since extensions usually cost money.",
      },
      {
        n: 17,
        title: "Survive underwriting",
        body: "Underwriting verifies income, assets, employment, and the property. Expect follow-up document requests. Do not change jobs, open credit, or move large sums between accounts during this window.",
      },
      {
        n: 18,
        title: "Clear title and review disclosures",
        body: "The title company searches for liens, easements, and ownership defects and issues a commitment. You will also receive seller disclosures and, three business days before closing, the Closing Disclosure. Compare it to your Loan Estimate line by line.",
      },
    ],
    checklist: [
      "Inspection completed and report read in full",
      "Repair or credit request submitted inside the contingency window",
      "Rate lock confirmed in writing with an expiration date",
      "Every underwriting document request answered within 24 hours",
      "No new credit, job changes, or large deposits",
      "Title commitment reviewed and homeowners insurance bound",
      "Closing Disclosure compared against the Loan Estimate",
    ],
  },
  {
    id: "closing-week",
    name: "Closing Week",
    duration: "3 to 7 days",
    summary:
      "The final stretch. Everything here is logistics, and the errors that happen in this week are the expensive ones.",
    steps: [
      {
        n: 19,
        title: "Do the final walkthrough",
        body: "Typically within 24 hours of closing. Confirm agreed repairs were done, the home is in the contracted condition, everything that conveys is still there, and every system works. Run water, test outlets, open the HVAC.",
      },
      {
        n: 20,
        title: "Sign, fund, and get the keys",
        body: "At the table you sign the note, the mortgage or deed of trust, and closing statements. Funds are wired, the deed is recorded, and keys transfer. Depending on the state and the recording schedule, that can be same-day or the next business day.",
      },
    ],
    checklist: [
      "Homeowners insurance policy active as of the closing date",
      "Wire instructions confirmed by phone using a number you looked up yourself",
      "Final cash-to-close amount confirmed and funds wired or certified",
      "Utilities scheduled to transfer on the closing date",
      "Address change filed with USPS, employer, bank, and DMV",
      "Government-issued photo ID packed",
      "Final walkthrough completed and any issues raised before signing",
    ],
  },
];

export interface Delay {
  title: string;
  what: string;
  doThis: string;
}

export const DELAYS: Delay[] = [
  {
    title: "Bidding wars",
    what: "Multiple offers push the price above list and sellers ask for best-and-final. This is the most common reason the house hunt stretches from weeks into months.",
    doThis:
      "Decide your walk-away number before you write, and compete on terms you can actually live with — closing date flexibility, a larger earnest deposit, a shorter inspection window — rather than blindly waiving protections.",
  },
  {
    title: "Inspection findings",
    what: "The report turns up roof, foundation, electrical, plumbing, or moisture issues, and negotiations restart days into the contract.",
    doThis:
      "Get contractor bids on anything major before you respond, so your request is a number and not an opinion. Ask for a credit instead of repairs when you want control over the work.",
  },
  {
    title: "Low appraisal",
    what: "The appraised value comes in under the contract price, so the lender will only finance against the lower value and a gap appears in your cash.",
    doThis:
      "You can renegotiate the price, split the difference, pay the gap in cash, request a reconsideration of value with better comparables, or exit if you kept the appraisal contingency.",
  },
  {
    title: "Underwriting document requests",
    what: "Conditions keep coming — a deposit that needs sourcing, a gap in employment, a bank statement page you did not send. Each round adds days.",
    doThis:
      "Answer every request within 24 hours, send complete documents including blank pages, and freeze your financial life: no new credit, no job changes, no unexplained transfers.",
  },
  {
    title: "Title problems",
    what: "The search surfaces a lien, an unreleased mortgage, an easement, a boundary dispute, or an heirship issue that has to be cleared before the deed can transfer.",
    doThis:
      "Let the title company work the cure and ask for a written timeline. Push your agent to extend the contract deadlines in writing rather than letting them lapse, and buy an owner's title policy.",
  },
];

export const FAQS = [
  {
    id: "earnest",
    q: "When is earnest money due, and can I lose it?",
    a: "Earnest money is typically due within one to three business days after the contract is fully executed, and it is delivered to the escrow or title company rather than to the seller. It is credited toward your cash to close. You can lose it if you walk away for a reason your contract does not protect, or if you miss a contingency deadline and then cancel. Staying inside your written deadlines is what keeps it safe.",
  },
  {
    id: "low-appraisal",
    q: "What does it mean if the appraisal comes in low?",
    a: "It means the appraiser valued the home below your contract price, and the lender will only lend against the lower number. That creates a gap between what you agreed to pay and what the loan will cover. Your options are to renegotiate the price, split the difference with the seller, cover the gap in cash, request a reconsideration of value with stronger comparable sales, or terminate if you kept an appraisal contingency.",
  },
  {
    id: "rate-lock",
    q: "When should I lock my mortgage rate?",
    a: "Most buyers lock once they are under contract and have a firm closing date, because locks generally run 30 to 60 days and extensions usually cost money. Locking earlier buys certainty; floating keeps the option to benefit if rates fall. Ask your loan officer for the lock period, the extension fee, and whether a float-down is available before you decide.",
  },
  {
    id: "bring-to-closing",
    q: "What do I need to bring to closing?",
    a: "A government-issued photo ID, proof that your cash to close has been wired or a certified check if your closer accepts one, and your copy of the Closing Disclosure. Bring your homeowners insurance declaration page if the lender has not already received it. Confirm wire instructions by phone using a number you looked up independently — wire fraud in real estate transactions is common and the money is rarely recoverable.",
  },
  {
    id: "utilities",
    q: "When should utilities be switched into my name?",
    a: "Schedule the transfer for the closing date itself. If your closing shifts, call and move the date rather than letting service start early or lapse. Some providers need several business days of notice, so make the calls during closing week rather than the day before.",
  },
];
